import { useEffect, useRef, useState } from 'react';
import type * as EchartsCore from 'echarts/core';
import type { EChartsCoreOption } from 'echarts/core';

/**
 * O MOTOR DE GRÁFICOS do 2.0 (ECharts, empacotado — nada de CDN).
 *
 * Carrega só as peças usadas e só quando uma tela de gráfico abre: o celular do balconista não
 * baixa gráfico que não vê. As cores, a fonte e o tooltip seguem o design do 2.0 (tokens).
 */

type Echarts = typeof EchartsCore;
let carregando: Promise<Echarts> | null = null;

function carregar(): Promise<Echarts> {
  carregando ??= (async () => {
    const [core, charts, comps, render] = await Promise.all([
      import('echarts/core'),
      import('echarts/charts'),
      import('echarts/components'),
      import('echarts/renderers'),
    ]);
    core.use([
      charts.BarChart,
      charts.LineChart,
      charts.PieChart,
      charts.GaugeChart,
      charts.RadarChart,
      charts.ScatterChart,
      charts.CandlestickChart,
      charts.HeatmapChart,
      charts.FunnelChart,
      comps.GridComponent,
      comps.TitleComponent,
      comps.PolarComponent,
      comps.RadarComponent,
      comps.TooltipComponent,
      comps.LegendComponent,
      comps.VisualMapComponent,
      comps.MarkLineComponent,
      comps.MarkAreaComponent,
      comps.DatasetComponent,
      render.SVGRenderer,
    ]);
    return core;
  })();
  return carregando;
}

/** A paleta do 2.0: marca, verde, ouro, azul, tinta — na ordem das séries. */
export const CORES = [
  '#cb1b2b',
  '#1a7f55',
  '#b7791f',
  '#1a5c9e',
  '#4b5157',
  '#e38b92',
  '#6fb89a',
  '#8e959b',
];

const TEMA = {
  color: CORES,
  textStyle: { fontFamily: "'IBM Plex Sans', system-ui, sans-serif", color: '#33383d' },
  tooltip: {
    backgroundColor: '#191c1f',
    borderWidth: 0,
    textStyle: { color: '#fff', fontSize: 12 },
    padding: [8, 10],
  },
};

/** Estilo da legenda — use em cada gráfico que TEM legenda (no tema ela apareceria em todos). */
export const LEGENDA = {
  textStyle: { color: '#4b5157', fontSize: 12 },
  icon: 'roundRect',
  itemWidth: 10,
  itemHeight: 10,
};

export function Grafico({
  opcao,
  altura = 280,
  rotulo,
}: {
  opcao: EChartsCoreOption;
  altura?: number;
  /** Descrição do gráfico para leitor de tela. */
  rotulo: string;
}) {
  const alvo = useRef<HTMLDivElement>(null);
  const instancia = useRef<ReturnType<Echarts['init']> | null>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let vivo = true;
    let observador: ResizeObserver | null = null;
    carregar().then(
      (ec) => {
        if (!vivo || !alvo.current) return;
        instancia.current = ec.init(alvo.current, null, { renderer: 'svg' });
        instancia.current.setOption({ ...TEMA, ...opcao }, true);
        observador = new ResizeObserver(() => instancia.current?.resize());
        observador.observe(alvo.current);
      },
      () => setFalhou(true),
    );
    return () => {
      vivo = false;
      observador?.disconnect();
      instancia.current?.dispose();
      instancia.current = null;
    };
    // a opção nova entra no efeito de baixo, sem recriar o gráfico
  }, []);

  useEffect(() => {
    instancia.current?.setOption({ ...TEMA, ...opcao }, true);
  }, [opcao]);

  if (falhou)
    return (
      <div className="gx-falhou" style={{ height: altura }}>
        Não foi possível desenhar o gráfico. Recarregue a página.
      </div>
    );
  return (
    <div ref={alvo} className="gx-area" style={{ height: altura }} role="img" aria-label={rotulo} />
  );
}
